import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setPerpsUpdate } from '../ReduxSlices/TradeSlice';

export const useSmartTradeStateDetector = () => {
  const dispatch = useDispatch();
  const lastKnownStatesRef = useRef(new Map());

  // Detect state changes by comparing current vs previous trade data
  const detectStateChanges = useCallback((newTrades) => {
    if (!Array.isArray(newTrades)) return false;

    const currentStates = new Map();
    let hasChanges = false;

    // Build current state map
    newTrades.forEach(trade => {
      currentStates.set(trade.user_trade_index, {
        is_open: trade.is_open,
        trade_type: trade.trade_type,
        tp: trade.tp,
        sl: trade.sl
      });
    });

    // Compare with last known states
    const lastStates = lastKnownStatesRef.current;

    // Check for changes
    for (const [tradeIndex, currentState] of currentStates) {
      const lastState = lastStates.get(tradeIndex);
      
      if (!lastState) {
        // New trade detected
        console.log('🆕 New trade detected:', tradeIndex);
        hasChanges = true;
      } else {
        // Check for specific state changes
        if (lastState.is_open && !currentState.is_open) {
          console.log('🎯 Trade closed (TP/SL hit):', tradeIndex);
          hasChanges = true;
          
          // Emit specific event for trade closure
          window.dispatchEvent(new CustomEvent('tradeClosedBySystem', {
            detail: { tradeIndex, reason: 'tp_sl_hit' }
          }));
        }
        
        if (lastState.trade_type === 'limit' && currentState.trade_type === 'trade' && currentState.is_open) {
          console.log('📈 Limit order executed:', tradeIndex);
          hasChanges = true;
          
          // Emit specific event for limit order execution
          window.dispatchEvent(new CustomEvent('limitOrderExecuted', {
            detail: { tradeIndex }
          }));
        }
      }
    }

    // Update stored states
    lastKnownStatesRef.current = currentStates;

    // If changes detected, trigger UI update
    if (hasChanges) {
      console.log('🔄 Trade state changes detected, updating UI...');
      dispatch(setPerpsUpdate());
      
      // Emit general state change event
      window.dispatchEvent(new CustomEvent('tradesStateChanged', {
        detail: { trades: newTrades, timestamp: Date.now() }
      }));
    }

    return hasChanges;
  }, [dispatch]);

  return { detectStateChanges };
};